import {
  ApplicationConfig,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import { provideHttpClient, withInterceptors, HttpClient, HttpBackend } from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { provideServiceWorker } from '@angular/service-worker';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

import { routes } from './app.routes';
import { environment } from '@/environments/environment';
import { PLAIN_HTTP_CLIENT } from './core/tokens/http-tokens';
import { XAppInterceptor } from './core/interceptors/x-app.interceptor';
import { ForbiddenInterceptor } from './core/interceptors/forbidden.interceptor';
import { ApiToastInterceptor } from './core/interceptors/api-toast.interceptor';
import FieldworkTheme from '@/themes/fieldwork-preset';
import { AppConfigService } from './core/services/app-config.service';
import { LanguageInitializerService } from './core/services/language-initializer.service';
import { ConfigAwareTranslateLoader } from './core/services/config-aware-translate-loader.service';

// API Proxies
import {
  API_BASE_URL,
  ConfigurationProxy,
  UserProxy,
  LookupProxy,
  LocationProxy,
  FieldworkProjectProxy,
  FieldworkEntryProxy,
  FieldworkNoteProxy,
  CountryProxy,
  FileProxy,
} from './proxy/proxies';

// Config-aware translate loader factory
const configAwareTranslateLoaderFactory = () => inject(ConfigAwareTranslateLoader);

export const appConfig: ApplicationConfig = {
  providers: [
    // HTTP Client with interceptors
    provideHttpClient(
      withInterceptors([XAppInterceptor, ForbiddenInterceptor, ApiToastInterceptor])
    ),
    
    // Plain HTTP Client without interceptors (for translation loading)
    {
      provide: PLAIN_HTTP_CLIENT,
      useFactory: (backend: HttpBackend) => new HttpClient(backend),
      deps: [HttpBackend],
    },
    
    // API Base URL
    {
      provide: API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    
    // API Proxies
    ConfigurationProxy,
    UserProxy,
    LookupProxy,
    LocationProxy,
    FieldworkProjectProxy,
    FieldworkEntryProxy,
    FieldworkNoteProxy,
    CountryProxy,
    FileProxy,
    
    // App initializer: load config first, then languages
    provideAppInitializer(async () => {
      const appConfigService = inject(AppConfigService);
      const languageInitializer = inject(LanguageInitializerService);

      try {
        await appConfigService.initializeApp();
        await languageInitializer.initializeLanguage();
        return true;
      } catch (error) {
        console.error('App initialization failed:', error);
        return false;
      }
    }),
    
    // Zone change detection
    provideZoneChangeDetection({ eventCoalescing: true }),
    
    // Router
    provideRouter(routes, withComponentInputBinding()),
    
    // PrimeNG
    providePrimeNG({
      theme: FieldworkTheme,
    }),
    
    // Message service for toasts
    MessageService,
    DialogService,
    
    // Translation
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: configAwareTranslateLoaderFactory,
        deps: [],
      },
    }),
    
    // Service Worker
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
